import type { PlanDoc } from '../../api/bindings/PlanDoc.ts';
import type { SectionWithBlocks } from '../../api/bindings/SectionWithBlocks.ts';
import { BlockView } from '../blocks/index.tsx';

export const PAGE_TITLES: Record<number, string> = {
  1: 'Business Model Canvas',
  2: 'Supporting Brief',
  3: 'SWOT & Market',
  4: 'Open Items',
};

function Section({ section }: { section: SectionWithBlocks }) {
  return (
    <section className="break-inside-avoid">
      <h3 className="mb-3 border-b border-gray-200 pb-1 text-lg font-semibold text-gray-800">
        {section.section.title}
      </h3>
      <div className="space-y-4">
        {section.blocks.map((block) => (
          <BlockView key={block.id} content={block.content} />
        ))}
      </div>
    </section>
  );
}

/**
 * The linear, print-ready plan document: all pages in order, one sheet per plan page
 * (`break-after-page`), confidentiality footer on every sheet. On screen the caller
 * can scope rendering to a single page via `onlyPage`.
 */
export default function PlanDocument({ doc, onlyPage }: { doc: PlanDoc; onlyPage?: number }) {
  const pages = [...new Set(doc.sections.map((s) => s.section.page))].sort((a, b) => a - b);
  const shown = onlyPage ? pages.filter((p) => p === onlyPage) : pages;
  const footer = doc.plan.confidentiality_footer ?? 'Confidential — internal working document';

  return (
    <div className="plan-document">
      {shown.map((page, i) => {
        const sections = doc.sections.filter((s) => s.section.page === page);
        return (
          <article
            key={page}
            className={`mx-auto max-w-3xl bg-white px-8 py-10 ${
              i < shown.length - 1 ? 'break-after-page' : ''
            }`}
          >
            <header className="mb-8 border-b-2 border-gray-800 pb-4">
              <p className="text-xs font-medium tracking-widest text-gray-400 uppercase">
                {doc.plan.business_name}
              </p>
              <h1 className="text-2xl font-bold tracking-tight text-gray-900">{doc.plan.title}</h1>
              <h2 className="mt-1 text-sm font-semibold text-gray-500">
                Page {page} — {PAGE_TITLES[page] ?? ''}
              </h2>
            </header>
            <div className="space-y-8">
              {sections.map((section) => (
                <Section key={section.section.id} section={section} />
              ))}
            </div>
            <footer className="mt-10 flex items-center justify-between border-t border-gray-200 pt-3 text-xs text-gray-400">
              <span>{footer}</span>
              <span>
                Page {page} of {pages.length}
              </span>
            </footer>
          </article>
        );
      })}
    </div>
  );
}
